/**
 * MatchWsClient — typed WebSocket client for the Phase 2 live match protocol.
 *
 * - Outgoing payloads are validated via ClientMessageSchema before send.
 * - Incoming payloads are validated via ServerMessageSchema; parse failures
 *   are silently dropped (with a console.warn) so a malformed broadcast
 *   never crashes the match UI.
 * - Heartbeats (`{ t: 'hb' }`) are sent every 5 s once the socket is OPEN.
 * - On unexpected close (anything that wasn't user-initiated via `close()`),
 *   we attempt one reconnect after 1 s. If that reconnect also fails, the
 *   caller's `onClose` is invoked so React can transition state.
 * - Observability: `onReconnecting` fires before the 1 s retry delay so the
 *   UI can flash a "재연결 중" banner; `onReconnected` fires once the retry
 *   socket reaches OPEN. These are signaling-only — the single-retry policy
 *   itself is unchanged.
 */
import {
  ClientMessageSchema,
  ServerMessageSchema,
  type ClientMessage,
  type ServerMessage,
} from '@sagwa/types'

const HEARTBEAT_INTERVAL_MS = 5_000
const RECONNECT_DELAY_MS = 1_000

export type MatchWsClientOptions = {
  wsUrl: string
  onMessage: (msg: ServerMessage) => void
  onClose: () => void
  /** Fired BEFORE the 1 s reconnect delay starts. UI uses this to show a banner. */
  onReconnecting?: () => void
  /** Fired once the reconnect socket reaches OPEN. UI uses this to flip to "restored". */
  onReconnected?: () => void
}

export class MatchWsClient {
  private readonly wsUrl: string
  private readonly onMessage: (msg: ServerMessage) => void
  private readonly onClose: () => void
  private readonly onReconnecting: (() => void) | null
  private readonly onReconnected: (() => void) | null
  private socket: WebSocket | null = null
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null
  private userClosed = false
  private hasAttemptedReconnect = false
  /** True while we're inside the reconnect attempt window; flips back on next OPEN. */
  private isReconnecting = false

  constructor(opts: MatchWsClientOptions) {
    this.wsUrl = opts.wsUrl
    this.onMessage = opts.onMessage
    this.onClose = opts.onClose
    this.onReconnecting = opts.onReconnecting ?? null
    this.onReconnected = opts.onReconnected ?? null
    this.connect()
  }

  /** Send a validated ClientMessage. Drops + warns if the socket is not OPEN. */
  send(msg: ClientMessage): void {
    const parsed = ClientMessageSchema.safeParse(msg)
    if (!parsed.success) {
      // eslint-disable-next-line no-console
      console.warn('[MatchWsClient] refusing to send invalid message', parsed.error)
      return
    }
    const s = this.socket
    if (!s || s.readyState !== WebSocket.OPEN) {
      // eslint-disable-next-line no-console
      console.warn('[MatchWsClient] send while not OPEN; dropping', msg.t)
      return
    }
    try {
      s.send(JSON.stringify(parsed.data))
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[MatchWsClient] send failed', err)
    }
  }

  /** Send a single heartbeat now. The internal interval already calls this. */
  heartbeat(): void {
    this.send({ t: 'hb' })
  }

  /** Force a reconnect using the same wsUrl. Used by the UI's retry button. */
  reconnect(): void {
    this.hasAttemptedReconnect = false
    this.isReconnecting = false
    this.teardownSocket()
    this.connect()
  }

  /** Close the socket without triggering reconnect. Idempotent. */
  close(): void {
    this.userClosed = true
    this.isReconnecting = false
    this.teardownSocket()
  }

  // ── internals ──────────────────────────────────────────────────────────────

  private connect(): void {
    try {
      const ws = new WebSocket(this.wsUrl)
      this.socket = ws
      ws.addEventListener('open', this.onOpen)
      ws.addEventListener('message', this.onSocketMessage)
      ws.addEventListener('close', this.onSocketClose)
      ws.addEventListener('error', this.onSocketError)
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[MatchWsClient] connect failed', err)
      this.handleUnexpectedClose()
    }
  }

  private onOpen = (): void => {
    if (this.heartbeatTimer !== null) clearInterval(this.heartbeatTimer)
    this.heartbeatTimer = setInterval(() => {
      this.heartbeat()
    }, HEARTBEAT_INTERVAL_MS)
    // If we were mid-reconnect, this OPEN means the retry succeeded.
    if (this.isReconnecting) {
      this.isReconnecting = false
      this.onReconnected?.()
    }
  }

  private onSocketMessage = (ev: MessageEvent): void => {
    if (typeof ev.data !== 'string') return
    let raw: unknown
    try {
      raw = JSON.parse(ev.data)
    } catch {
      // eslint-disable-next-line no-console
      console.warn('[MatchWsClient] non-JSON frame; ignoring')
      return
    }
    const parsed = ServerMessageSchema.safeParse(raw)
    if (!parsed.success) {
      // eslint-disable-next-line no-console
      console.warn('[MatchWsClient] invalid ServerMessage; ignoring', parsed.error)
      return
    }
    this.onMessage(parsed.data)
  }

  private onSocketError = (): void => {
    // The browser already fires 'close' after 'error'; nothing to do here.
  }

  private onSocketClose = (): void => {
    if (this.heartbeatTimer !== null) {
      clearInterval(this.heartbeatTimer)
      this.heartbeatTimer = null
    }
    if (this.userClosed) return
    this.handleUnexpectedClose()
  }

  private handleUnexpectedClose(): void {
    if (this.hasAttemptedReconnect) {
      this.isReconnecting = false
      this.onClose()
      return
    }
    this.hasAttemptedReconnect = true
    this.isReconnecting = true
    // Notify the UI BEFORE the 1 s delay so the banner appears immediately.
    this.onReconnecting?.()
    if (this.reconnectTimer !== null) clearTimeout(this.reconnectTimer)
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null
      this.connect()
    }, RECONNECT_DELAY_MS)
  }

  private teardownSocket(): void {
    if (this.reconnectTimer !== null) {
      clearTimeout(this.reconnectTimer)
      this.reconnectTimer = null
    }
    if (this.heartbeatTimer !== null) {
      clearInterval(this.heartbeatTimer)
      this.heartbeatTimer = null
    }
    const s = this.socket
    if (!s) return
    s.removeEventListener('open', this.onOpen)
    s.removeEventListener('message', this.onSocketMessage)
    s.removeEventListener('close', this.onSocketClose)
    s.removeEventListener('error', this.onSocketError)
    try {
      if (s.readyState === WebSocket.OPEN || s.readyState === WebSocket.CONNECTING) {
        s.close()
      }
    } catch {
      /* no-op */
    }
    this.socket = null
  }
}
